import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getTokenFromStorage, showError } from '../utility/common-logic';
import { clearLocalStorage } from '../utility/local-storage';
import { ApiService } from './api-services/api.service';

interface ChatClientPostMessage {
  action?: string;
  source?: string;
}

interface ChatClientSettings {
  isShow?: boolean;
  devUrl?: string;
  prodDomains?: Record<string, string>;
  anonymousPath?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatClientService implements OnDestroy {
  private panelOpenSubject = new BehaviorSubject(false);
  private iframeUrlSubject = new BehaviorSubject<string | null>(null);
  private loadingSubject = new BehaviorSubject(false);
  private openAttemptId = 0;
  private lastAuthenticated: boolean | null = null;

  panelOpen$ = this.panelOpenSubject.asObservable();
  iframeUrl$ = this.iframeUrlSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  openingPanel = false;

  private handleChatClientMessage = (event: MessageEvent) => {
    if (!this.isEnabled || !this.isPanelOpen) {
      return;
    }

    if (!this.buildAllowedOrigins().has(event.origin)) {
      return;
    }

    const data = this.parsePostMessage(event.data);
    if (data?.source !== 'aira') {
      return;
    }

    if (data.action === 'close_chat') {
      this.ngZone.run(() => this.close());
    } else if (data.action === 'session_expired') {
      // The embedded chat client's deeptoken session expired (token invalid).
      // The user authenticates in the PWA, so log them out of the PWA itself.
      this.ngZone.run(() => this.handleSessionExpired());
    }
  };

  constructor(
    private api: ApiService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private ngZone: NgZone,
    private router: Router,
  ) {
    window.addEventListener('message', this.handleChatClientMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleChatClientMessage);
  }

  private get chatClient(): ChatClientSettings | undefined {
    return environment.settings.chatClient as ChatClientSettings | undefined;
  }

  get isEnabled(): boolean {
    return !!this.chatClient?.isShow;
  }

  get isPanelOpen(): boolean {
    return this.panelOpenSubject.value;
  }

  private get hasAuthToken(): boolean {
    return !!getTokenFromStorage();
  }

  syncSession(isAuthenticated: boolean = this.hasAuthToken): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.lastAuthenticated === null) {
      this.lastAuthenticated = isAuthenticated;
      return;
    }

    if (this.lastAuthenticated === isAuthenticated) {
      return;
    }

    const wasOpen = this.isPanelOpen || this.openingPanel;
    this.lastAuthenticated = isAuthenticated;
    this.close();

    if (wasOpen && isAuthenticated) {
      this.open();
    }
  }

  resetOnLogout(): void {
    if (!this.isEnabled) {
      return;
    }

    this.lastAuthenticated = false;
    this.close();
  }

  open(): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.isPanelOpen) {
      this.close();
      return;
    }

    if (!this.baseUrl) {
      showError(
        this.translate.instant('msg.error._'),
        this.toastr,
        this.translate,
        true,
      );
      return;
    }

    const hasToken = this.hasAuthToken;
    this.lastAuthenticated = hasToken;

    const attemptId = ++this.openAttemptId;
    this.markOpeningPanel();

    if (!hasToken) {
      this.showPanel(this.getAnonymousUrl());
      return;
    }

    this.loadingSubject.next(true);

    this.api
      .getDeepLinkToken()
      .then((res) => {
        if (!this.isOpenAttemptActive(attemptId)) {
          return;
        }

        if (res?.deeplink_token) {
          this.showPanel(
            `${this.baseUrl}/?deeplink_token=${encodeURIComponent(res.deeplink_token)}`,
          );
        } else {
          this.openAnonymousOrFail();
        }
      })
      .catch((err) => {
        if (!this.isOpenAttemptActive(attemptId)) {
          return;
        }

        this.openAnonymousOrFail(err);
      })
      .finally(() => {
        if (attemptId !== this.openAttemptId) {
          return;
        }

        this.loadingSubject.next(false);
      });
  }

  close(): void {
    this.openAttemptId++;
    this.resetPanelState();
  }

  toggle(): void {
    if (this.isPanelOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Log the user out of the PWA when the embedded chat client reports an
   * expired session. The token is already invalid server-side (that's what
   * triggered session_expired), so we skip the logout API call — it would just
   * 401 — and do a local logout: close the chat, clear local + cache storage,
   * and route to login.
   */
  private handleSessionExpired(): void {
    this.close();
    clearLocalStorage();
    this.api.clearCacheStorage();
    this.router.navigate(['/login']);
  }

  get baseUrl(): string {
    if (!environment.production) {
      return (this.chatClient?.devUrl || '').replace(/\/$/, '');
    }

    const prodDomains = this.chatClient?.prodDomains;
    if (!prodDomains) {
      return '';
    }

    const host = window.location.hostname || '';
    const tld = host.split('.').pop() || 'com';
    const mapped = (prodDomains[tld] || prodDomains['com'] || '').trim();
    return mapped ? mapped.replace(/\/$/, '') : '';
  }

  private isOpenAttemptActive(attemptId: number): boolean {
    return attemptId === this.openAttemptId && this.openingPanel;
  }

  private openAnonymousOrFail(err?: unknown): void {
    if (!getTokenFromStorage()) {
      this.showPanel(this.getAnonymousUrl());
      return;
    }

    this.failOpenAttempt();
    if (err !== undefined) {
      showError(err, this.toastr, this.translate);
    } else {
      showError(
        this.translate.instant('msg.error._'),
        this.toastr,
        this.translate,
        true,
      );
    }
  }

  private failOpenAttempt(): void {
    this.resetPanelState();
  }

  private resetPanelState(): void {
    this.panelOpenSubject.next(false);
    this.iframeUrlSubject.next(null);
    this.loadingSubject.next(false);
    this.openingPanel = false;
    document.body.classList.remove('chat-ui-panel-open');
  }

  private showPanel(url: string): void {
    if (!url?.trim()) {
      this.failOpenAttempt();
      return;
    }

    this.iframeUrlSubject.next(url);
    this.panelOpenSubject.next(true);
    this.loadingSubject.next(false);
    this.openingPanel = false;
    document.body.classList.add('chat-ui-panel-open');
  }

  private markOpeningPanel(): void {
    document.body.classList.add('chat-ui-panel-open');
    this.openingPanel = true;
    this.panelOpenSubject.next(true);
  }

  private getAnonymousUrl(): string {
    const path = this.chatClient?.anonymousPath || '/anonymous';
    const anonymousPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${anonymousPath}`;
  }

  private buildAllowedOrigins(): Set<string> {
    const origins = new Set<string>();

    this.addOriginFromUrl(this.chatClient?.devUrl, origins);

    const prodDomains = this.chatClient?.prodDomains;
    if (prodDomains) {
      Object.values(prodDomains).forEach((domainUrl) =>
        this.addOriginFromUrl(domainUrl, origins),
      );
    }

    this.addOriginFromUrl(this.baseUrl, origins);

    return origins;
  }

  private addOriginFromUrl(
    url: string | undefined,
    origins: Set<string>,
  ): void {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return;
    }

    const normalized = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

    try {
      const parsed = new URL(normalized);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        origins.add(parsed.origin);
      }
    } catch {
      return;
    }
  }

  private parsePostMessage(data: unknown): ChatClientPostMessage | null {
    if (!data) {
      return null;
    }

    if (typeof data === 'string') {
      try {
        return JSON.parse(data) as ChatClientPostMessage;
      } catch {
        return null;
      }
    }

    if (typeof data === 'object') {
      return data as ChatClientPostMessage;
    }

    return null;
  }
}
